import { Alert, Badge, Button, Card, Field, Select, TextInput } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../api/client.js';
import { keys, useTenant, type TenantSettings } from '../../api/hooks.js';
import { DataTable, type Column } from '../../components/admin/DataTable.js';
import { DocumentVectorsStatus } from '../../components/admin/DocumentVectors.js';
import { WebPolicyCard } from '../../components/admin/IntegrationCards.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { EmailSection } from '../../components/settings/sections.js';
import { useCapabilities } from '../../experience.js';
import { timeZones } from '../../time-zone.js';
import { AdminLayout } from './AdminLayout.js';

/** A tenant switch saved at once; the answer is the new settings. */
function useTenantSave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api<TenantSettings>('PATCH', '/api/tenant', body),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.tenant, data);
      void queryClient.invalidateQueries({ queryKey: keys.models });
      void queryClient.invalidateQueries({ queryKey: keys.preferences });
    },
  });
}

/**
 * D-58: "words + meaning" (document text goes to the embedding service on
 * Bulutistan) or "words only" (nothing leaves our servers). Both explained
 * in plain words; switching to words only removes the stored vectors.
 */
function DocumentSearchCard() {
  const { t } = useTranslation();
  const tenant = useTenant();
  const save = useTenantSave();
  const [confirm, setConfirm] = useState(false);
  const mode = tenant.data?.documentSearch ?? 'hybrid';
  const option = (value: 'hybrid' | 'keyword') => (
    <label className="switch-row" key={value}>
      <input
        type="radio"
        name="document-search"
        value={value}
        checked={mode === value}
        disabled={!tenant.data || save.isPending}
        onChange={() => (value === 'keyword' ? setConfirm(true) : save.mutate({ documentSearch: 'hybrid' }))}
        data-testid={`document-search-${value}`}
      />
      <span>
        <strong>{t(`policies.documentSearch.${value}.title`)}</strong> {mode === value ? <Badge tone="success">{t('policies.active')}</Badge> : null}
        <br />
        <span className="muted">{t(`policies.documentSearch.${value}.hint`)}</span>
      </span>
    </label>
  );
  return (
    <Card title={t('policies.documentSearch.title')} data-testid="document-search">
      <ErrorAlert error={tenant.error ?? save.error} />
      {option('hybrid')}
      {/* D-253: why documents have no vectors, right under the choice that sends them to the embedding service */}
      {mode === 'hybrid' ? <DocumentVectorsStatus /> : null}
      {option('keyword')}
      {confirm ? (
        <Alert
          tone="warning"
          title={t('policies.documentSearch.confirm')}
          action={
            <span className="row">
              <Button
                size="sm"
                variant="danger"
                busy={save.isPending}
                onClick={() => save.mutate({ documentSearch: 'keyword' }, { onSuccess: () => setConfirm(false) })}
                data-testid="document-search-confirm"
              >
                {t('policies.documentSearch.switch')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
                {t('common.cancel')}
              </Button>
            </span>
          }
        />
      ) : null}
    </Card>
  );
}

/**
 * MM-006 until 16c.1: which chat models members may pick, and who may use each, is Yönetim › Modeller's now ("Sohbette",
 * "Kimler kullanabilir"); the old list became those switches. This card points there.
 */
function AllowedModelsCard() {
  const { t } = useTranslation();
  return (
    <Card title={t('policies.models.title')} data-testid="allowed-models">
      <p className="muted">{t('policies.models.moved')}</p>
      <div>
        <Link to="/admin/models" className="ui-button ui-button--secondary ui-button--sm" data-testid="allowed-models-open">
          {t('policies.models.open')}
        </Link>
      </div>
    </Card>
  );
}

/** D-23, SA-061, SA-008 and the 2026-10-27 decision: approvals, full access, memory, trash, time zone. */
function ApprovalsCard() {
  const { t } = useTranslation();
  const tenant = useTenant();
  const save = useTenantSave();
  const [days, setDays] = useState<string | null>(null);
  const data = tenant.data;
  const flag = (key: 'requireCodeApproval' | 'allowFullAccess' | 'memoryAllowed' | 'allowPersonalProviders', testId: string, fallback: boolean) => (
    <label className="switch-row">
      <input type="checkbox" checked={data?.[key] ?? fallback} disabled={!data || save.isPending} onChange={(e) => save.mutate({ [key]: e.target.checked })} data-testid={testId} />
      <span>
        <strong>{t(`policies.flags.${key}.title`)}</strong>
        <span className="muted"> — {t(`policies.flags.${key}.hint`)}</span>
      </span>
    </label>
  );
  return (
    <Card title={t('policies.approvals.title')} data-testid="tenant-switches">
      <ErrorAlert error={tenant.error ?? save.error} />
      {flag('requireCodeApproval', 'require-code-approval', false)}
      {flag('allowFullAccess', 'allow-full-access', false)}
      {flag('memoryAllowed', 'memory-allowed', true)}
      {flag('allowPersonalProviders', 'allow-personal-providers', false)}
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          const value = (days ?? '').trim();
          save.mutate({ trashRetentionDays: value ? Number(value) : null }, { onSuccess: () => setDays(null) });
        }}
      >
        <Field label={t('team.switches.trash')} hint={t('team.switches.trashHint')}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="number"
              min={1}
              max={365}
              value={days ?? (data?.trashRetentionDays ? String(data.trashRetentionDays) : '')}
              placeholder="30"
              onChange={(e) => setDays(e.target.value)}
              data-testid="trash-days"
            />
          )}
        </Field>
        <Button type="submit" size="sm" busy={save.isPending} disabled={days === null}>
          {t('common.save')}
        </Button>
      </form>
      <Field label={t('team.switches.timeZone')} hint={t('team.switches.timeZoneHint')}>
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={data?.defaultTimeZone ?? 'Europe/Istanbul'}
            disabled={!data || save.isPending}
            onChange={(e) => save.mutate({ defaultTimeZone: e.target.value })}
            data-testid="default-time-zone"
          >
            {(data && !timeZones().includes(data.defaultTimeZone) ? [data.defaultTimeZone, ...timeZones()] : timeZones()).map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </Card>
  );
}

/** SA-064: answers liked and disliked per model in the last 30 days; counts only. */
/** Yanıt değerlendirmeleri (Kullanım ve limitler, SA-064): per model the thumbs up and down and the notes; counts only. */
export function FeedbackCard({ days = 30 }: { days?: number }) {
  const { t, i18n } = useTranslation();
  const summary = useQuery({
    queryKey: ['tenant', 'feedback', days],
    queryFn: () => api<{ days: number; models: { model: string; up: number; down: number; withNote: number }[] }>('GET', `/api/tenant/feedback-summary?days=${days}`),
  });
  type Row = { model: string; up: number; down: number; withNote: number };
  const columns: Column<Row>[] = [
    { id: 'model', header: t('team.feedback.columns.model'), cell: (row) => row.model },
    { id: 'up', header: t('team.feedback.columns.up'), width: 'sm', numeric: true, cell: (row) => row.up.toLocaleString(i18n.language) },
    { id: 'down', header: t('team.feedback.columns.down'), width: 'sm', numeric: true, cell: (row) => row.down.toLocaleString(i18n.language) },
    { id: 'notes', header: t('team.feedback.columns.notes'), width: 'sm', numeric: true, cell: (row) => row.withNote.toLocaleString(i18n.language) },
  ];
  return (
    <section className="stack" data-testid="feedback-summary">
      <p className="muted">{t('team.feedback.hint')}</p>
      <ErrorAlert error={summary.error} />
      {summary.data ? (
        <DataTable label={t('team.feedback.title')} columns={columns} rows={summary.data.models} rowKey={(row) => row.model} empty={t('team.feedback.empty')} />
      ) : null}
    </section>
  );
}

/** What the organization's people receive by e-mail (D-100: here since the settings dialog keeps the personal sections only). */
function EmailCard() {
  const { t } = useTranslation();
  return (
    <Card title={t('settingsDialog.sections.email')} data-testid="policies-email">
      <EmailSection />
    </Card>
  );
}

/** The organization's policies (Yönetim → Onay politikaları). */
export function PoliciesContent() {
  const capabilities = useCapabilities();
  return (
    <div className="stack">
      <ApprovalsCard />
      <DocumentSearchCard />
      <AllowedModelsCard />
      <WebPolicyCard />
      {capabilities.publicSignup ? <EmailCard /> : null}
    </div>
  );
}

export function PoliciesPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout section="policies" title={t('admin.sections.policies')} intro={t('policies.intro')}>
      <PoliciesContent />
    </AdminLayout>
  );
}
